import { useEffect, useRef, useState } from 'react';
import { useReducedMotion } from 'motion/react';

interface TypewriterOptions {
  /** Milliseconds per character. */
  speed?: number;
  /** Milliseconds to wait before typing starts. */
  startDelay?: number;
  /** Gate typing (e.g. start only once the element is in view). */
  enabled?: boolean;
}

/**
 * Progressively reveals `text` one character at a time using a SINGLE timer
 * (writes a substring by index — never one setState per character storm).
 * Honors reduced-motion by returning the full string immediately. Pair the
 * rendered output with the `.typewriter-caret` utility for the blinking caret.
 */
export const useTypewriter = (
  text: string,
  { speed = 18, startDelay = 0, enabled = true }: TypewriterOptions = {}
): { text: string; isDone: boolean } => {
  const reduce = useReducedMotion();
  const [output, setOutput] = useState('');
  const [isDone, setIsDone] = useState(false);
  const indexRef = useRef(0);

  useEffect(() => {
    if (!enabled) return;

    if (reduce) {
      setOutput(text);
      setIsDone(true);
      return;
    }

    setOutput('');
    setIsDone(false);
    indexRef.current = 0;

    let intervalId: ReturnType<typeof setInterval> | undefined;
    const startId = window.setTimeout(() => {
      intervalId = setInterval(() => {
        indexRef.current += 1;
        setOutput(text.slice(0, indexRef.current));
        if (indexRef.current >= text.length) {
          clearInterval(intervalId);
          setIsDone(true);
        }
      }, speed);
    }, startDelay);

    return () => {
      window.clearTimeout(startId);
      if (intervalId) clearInterval(intervalId);
    };
  }, [text, speed, startDelay, enabled, reduce]);

  return { text: output, isDone };
};
